// Code Next P1 — "Write it in C": check one step's C against the answer key.
//
// The key is codegen's own lines for the step (utils/codeNextKey.js). The
// learner's statement(s) and the key are read by the SAME parser — the
// preview's C reader (preview/cparse.js), wrapped in a dummy function — then
// split into parts and compared part by part:
//
//   Set        target, operator, value. x = x + 1 = x += 1 = x++ = ++x;
//              x = x - 1 …; + and * in either order.
//   Show       the printf format (unescaped), each % code and its value.
//              %.2f against %f is a REAL difference, named from the step.
//   Read       one scanf per value (one combined scanf is accepted, with a
//              note); & on everything but a char array; " %c".
//   Give back  return expr; (+ and * in either order).
//   Decision   the if / else if / else lines only (the steps inside are
//              their own steps): how many checks, each check (a > b = b < a),
//              whether it ends in else.
//   Repeat     the loop line and its } only: while (check) — the check
//              either way round; for (start; check; step) — the counter
//              declared there, where it starts, the check, i++ = i += 1.
//   Use / Do   name(values); or keep = name(values); — which flow (or C
//              function), each value in order (& where it changes it), where
//              the result is kept.
//   Declare    a Resources card: one declaration per value, in any order —
//              the type, [size] for a list, = start value where it has one.
//   Switch     its own lines: switch (x) {, each case v:, each break; (a
//              path that stops here), default:, } — in order.
//   Prototype  the same line ending in ; (names may be left out).
//   Flow       a flow's first line, float hra(float basic) { — what it gives
//              back, its name, each value it takes (type and name), the {.
//
// Verdicts (decision 2 — correct C that differs from the step's sentence is
// accepted with a gentle note):
//   match       "✓ matches"
//   works       "✓ works — same as count++"
//   wrong       "✗ …" with one specific fix in plain words
//   unreadable  "can't read this line" (a syntax error)
//
// Pure: no React, no store.
import { parseProgram, CParseError } from '@/preview/cparse';

export const CHECKED_TYPES = ['assignment', 'output', 'input', 'return', 'decision', 'loop', 'subflow', 'function', 'declare', 'prototype', 'action', 'switch'];
export const isCheckedType = (type) => CHECKED_TYPES.includes(type);

// ── reading ───────────────────────────────────────────────────────────────

/** C statement text → statements, or throws CParseError. */
export function readStatements(text) {
  const unit = parseProgram(`void __cn_step(void) {\n${text}\n}\n`);
  const fn = unit.functions.get('__cn_step');
  return fn ? fn.body.items.filter((s) => s.s !== 'empty') : [];
}

/** The line a parse error is on, counted in the learner's text (1-based). */
const errLine = (e) => (e && Number.isFinite(e.line) ? Math.max(1, e.line - 1) : null);

function tryRead(text) {
  try {
    return { stmts: readStatements(text) };
  } catch (e) {
    if (!(e instanceof CParseError) && !(e && e.isCParse)) return { error: e, line: null };
    return { error: e, line: errLine(e) };
  }
}

// ── expressions, canonical ────────────────────────────────────────────────

const numText = (v) => {
  const s = String(v).replace(/[uUlLfF]+$/, '');
  const n = Number(s);
  return Number.isFinite(n) && /^[0-9.eE+-]+$/.test(s) ? String(n) : String(v);
};

const COMMUTATIVE = new Set(['+', '*', '==', '!=']);

function flatten(e, op) {
  if (e && e.t === 'group') return flatten(e.x, op);
  if (e && e.t === 'bin' && e.op === op) return [...flatten(e.l, op), ...flatten(e.r, op)];
  return [e];
}

/**
 * An expression as a string. `loose` sorts the operands of + * == != so the
 * order does not matter; strict keeps it (for "matches" against "works").
 */
export function canon(e, loose = true) {
  if (!e) return '';
  const c = (x) => canon(x, loose);
  switch (e.t) {
    case 'group': return c(e.x);
    case 'num': return numText(e.v);
    case 'chr': return `'${e.v}'`;
    case 'str': return JSON.stringify(e.v);
    case 'var': return e.v;
    case 'bin': {
      if (loose && COMMUTATIVE.has(e.op)) {
        const parts = flatten(e, e.op).map(c);
        parts.sort();
        return `(${parts.join(` ${e.op} `)})`;
      }
      return `(${c(e.l)} ${e.op} ${c(e.r)})`;
    }
    case 'neg': return `-${c(e.x)}`;
    case 'plus': return c(e.x);
    case 'not': return `!${c(e.x)}`;
    case 'bitnot': return `~${c(e.x)}`;
    case 'addr': return `&${c(e.x)}`;
    case 'deref': return `*${c(e.x)}`;
    case 'index': return `${c(e.x)}[${c(e.idx)}]`;
    case 'member': return `${c(e.x)}${e.arrow ? '->' : '.'}${e.name}`;
    case 'call': return `${e.fn}(${(e.args || []).map(c).join(', ')})`;
    case 'callx': return `${c(e.f)}(${(e.args || []).map(c).join(', ')})`;
    case 'cast': return `(${(e.ty && e.ty.name) || 'type'})${c(e.x)}`;
    case 'size': return `sizeof ${c(e.x)}`;
    case 'sizeType': return `sizeof(${(e.ty && e.ty.name) || 'type'})`;
    case 'choose': return `(${c(e.c)} ? ${c(e.a)} : ${c(e.b)})`;
    case 'inc': return e.pre ? `${e.op}${c(e.x)}` : `${c(e.x)}${e.op}`;
    case 'assign': return `${c(e.l)} ${e.op} ${c(e.r)}`;
    case 'seq': return (e.items || []).map(c).join(', ');
    default: return JSON.stringify(e);
  }
}

/** Printable C for a tree (for messages): the strict canonical form, minus outer brackets. */
export const show = (e) => canon(e, false).replace(/^\((.*)\)$/, '$1');

// ── Set ───────────────────────────────────────────────────────────────────

const OP_OF_BIN = { '+': '+=', '-': '-=', '*': '*=', '/': '/=', '%': '%=' };

/**
 * One assignment statement → { target, op, value (canon), valueTree, literal }.
 * x++ / ++x → x += 1; x = x + v (or v + x) → x += v; x = x - v → x -= v.
 * `literal` is the form as written, so "matches" can tell an equivalent apart.
 */
export function setParts(stmt) {
  if (!stmt || stmt.s !== 'expr') return null;
  let e = stmt.e;
  while (e && e.t === 'group') e = e.x;
  if (!e) return null;
  if (e.t === 'inc') {
    return {
      target: canon(e.x), targetText: show(e.x), op: e.op === '++' ? '+=' : '-=', value: '1',
      literal: e.pre ? `${e.op}${canon(e.x)}` : `${canon(e.x)}${e.op}`, written: e.pre ? `${e.op}${show(e.x)}` : `${show(e.x)}${e.op}`,
    };
  }
  if (e.t !== 'assign') return null;
  const target = canon(e.l);
  const literal = `${target} ${e.op} ${canon(e.r, false)}`;
  const written = `${show(e.l)} ${e.op} ${show(e.r)}`;
  if (e.op === '=') {
    let r = e.r;
    while (r && r.t === 'group') r = r.x;
    if (r && r.t === 'bin' && OP_OF_BIN[r.op]) {
      if (r.op === '+' || r.op === '*') {
        const parts = flatten(r, r.op);
        const at = parts.findIndex((p) => canon(p) === target);
        if (at >= 0) {
          const rest = parts.filter((_, i) => i !== at).map((p) => canon(p)).sort();
          const value = rest.length === 1 ? rest[0] : `(${rest.join(` ${r.op} `)})`;
          return { target, targetText: show(e.l), op: OP_OF_BIN[r.op], value, literal, written };
        }
      } else if (canon(r.l) === target) {
        return { target, targetText: show(e.l), op: OP_OF_BIN[r.op], value: canon(r.r), literal, written };
      }
    }
  }
  // x += a + b: the value's own + order does not matter.
  return { target, targetText: show(e.l), op: e.op, value: canon(e.r), valueTree: e.r, literal, written };
}

const OP_WORDS = {
  '=': 'sets', '+=': 'increases', '-=': 'decreases', '*=': 'multiplies', '/=': 'divides', '%=': 'keeps the remainder of',
};

function checkSet(want, got, ctx) {
  if (got.length !== want.length) {
    return wrong(want.length === 1
      ? 'This step is one line: one assignment ending in ;'
      : `This step is ${want.length} lines, one for each value it sets.`);
  }
  let works = null;
  for (let i = 0; i < want.length; i += 1) {
    const w = setParts(want[i]);
    const g = setParts(got[i]);
    if (!w) return null; // the key is not a plain assignment: generic compare
    if (!g) {
      return wrong(`Write it as an assignment: ${w.targetText} on the left, then = (or +=), the value, and ;`);
    }
    if (g.target !== w.target) {
      return wrong(`This line should change ${w.targetText} — yours changes ${g.targetText}.`);
    }
    if (g.op !== w.op || g.value !== w.value) {
      if (g.op === '=' && w.op !== '=' && g.value === w.value) {
        return wrong(`That replaces ${w.targetText} with the value. The step ${OP_WORDS[w.op] || 'changes'} ${w.targetText} — use ${w.op} (or ${w.targetText} = ${w.targetText} ${w.op[0]} …).`);
      }
      if (g.op !== w.op) {
        return wrong(`The step ${OP_WORDS[w.op] || 'changes'} ${w.targetText}${ctx.sentence ? ` ("${ctx.sentence}")` : ''} — check the operator.`);
      }
      return wrong(`${w.targetText} gets a different value. Check the right-hand side against the step${ctx.sentence ? `: "${ctx.sentence}"` : ''}.`);
    }
    if (g.literal !== w.literal) works = works || w.written;
  }
  return works ? { verdict: 'works', sameAs: works } : { verdict: 'match' };
}

// ── printf / scanf formats ────────────────────────────────────────────────

/** C string-literal body → its characters. */
export function unescapeC(s) {
  return String(s || '').replace(/\\(n|t|r|0|\\|"|'|a|b|f|v)/g, (m, c) => (
    { n: '\n', t: '\t', r: '\r', 0: '\0', '\\': '\\', '"': '"', "'": "'", a: '\x07', b: '\b', f: '\f', v: '\v' }[c]
  ));
}

const SPEC_RE = /%([-+ #0]*)(\d+|\*)?(?:\.(\d+|\*))?(hh|h|ll|l|L|z|j|t)?([diouxXfFeEgGaAcspn%]|\[[^\]]*\])/g;

/** A format → { text: the words with each % code as \u0001, specs: [{ raw, conv, len, precision, skipWs }] }. */
export function splitFormat(fmt) {
  const specs = [];
  let text = '';
  let last = 0;
  let m;
  SPEC_RE.lastIndex = 0;
  while ((m = SPEC_RE.exec(fmt)) !== null) {
    const before = fmt.slice(last, m.index);
    text += before;
    last = m.index + m[0].length;
    if (m[5] === '%') { text += '%'; continue; }
    text += '\u0001';
    specs.push({
      raw: m[0], flags: m[1] || '', width: m[2] || '', precision: m[3] === undefined ? null : m[3],
      len: m[4] || '', conv: m[5], skipWs: /\s$/.test(text.slice(0, -1)),
    });
  }
  text += fmt.slice(last);
  return { text, specs };
}

const KIND_OF_CONV = {
  d: 'a whole number', i: 'a whole number', u: 'a whole number', f: 'a decimal', F: 'a decimal', e: 'a decimal', g: 'a decimal',
  c: 'one character', s: 'text', x: 'a whole number in hex',
};
export const kindOfSpec = (s) => (s.conv && s.conv.startsWith('[') ? 'a line of text' : KIND_OF_CONV[s.conv] || 'a value');
const nth = (i) => ['1st', '2nd', '3rd'][i] || `${i + 1}th`;

/** A call statement `name(args);` → { args } or null. */
function callOf(stmt, name) {
  if (!stmt || stmt.s !== 'expr') return null;
  let e = stmt.e;
  while (e && e.t === 'group') e = e.x;
  if (!e || e.t !== 'call' || e.fn !== name) return null;
  return e;
}

/** printf → { fmt (unescaped), split, args }. */
export function printfParts(stmt) {
  const c = callOf(stmt, 'printf');
  if (!c || !c.args.length || c.args[0].t !== 'str') return null;
  const fmt = unescapeC(c.args[0].v);
  return { fmt, split: splitFormat(fmt), args: c.args.slice(1) };
}

const sameSpecPrintf = (a, b) => {
  const norm = (s) => ({ ...s, conv: s.conv === 'i' ? 'd' : s.conv, len: s.conv === 'f' && s.len === 'l' ? '' : s.len, skipWs: false, raw: '' });
  const x = norm(a); const y = norm(b);
  return x.conv === y.conv && x.len === y.len && x.precision === y.precision && x.width === y.width && x.flags === y.flags;
};

function wordsDiffer(wantText, gotText) {
  const strip = (t) => t.replace(/\u0001/g, '…');
  if (wantText.replace(/\n$/, '') === gotText.replace(/\n$/, '')) {
    return wantText.endsWith('\n')
      ? 'Add \\n at the end of the text, so what comes next starts on a new line.'
      : 'This step does not end the line: take the \\n off the end.';
  }
  return `The words differ. The step shows: "${strip(wantText).replace(/\n/g, '\\n')}"`;
}

function checkShow(want, got) {
  if (want.length !== got.length) {
    return wrong(want.length === 1 ? 'This step is one printf(…); line.' : `This step is ${want.length} printf lines.`);
  }
  let works = null;
  for (let i = 0; i < want.length; i += 1) {
    const w = printfParts(want[i]);
    if (!w) return null;
    const g = printfParts(got[i]);
    if (!g) return wrong('Show it with printf: printf("your words", values);');
    const ws = w.split.specs;
    const gs = g.split.specs;
    if (gs.length !== g.args.length) {
      return wrong(`Your text has ${gs.length} % code${gs.length === 1 ? '' : 's'} but ${g.args.length} value${g.args.length === 1 ? '' : 's'} after it — one value for each % code.`);
    }
    if (gs.length !== ws.length) {
      return wrong(`The step shows ${ws.length} value${ws.length === 1 ? '' : 's'} — your text has ${gs.length} % code${gs.length === 1 ? '' : 's'}.`);
    }
    if (w.split.text !== g.split.text) return wrong(wordsDiffer(w.split.text, g.split.text));
    for (let k = 0; k < ws.length; k += 1) {
      const name = show(w.args[k]);
      if (canon(g.args[k]) !== canon(w.args[k])) {
        return wrong(`The ${nth(k)} % code should show ${name}.`);
      }
      if (!sameSpecPrintf(ws[k], gs[k])) {
        if (ws[k].precision !== null && ws[k].precision !== gs[k].precision && ws[k].conv === gs[k].conv) {
          return wrong(`Your step shows ${name} with ${ws[k].precision} decimal${ws[k].precision === '1' ? '' : 's'} — which % code gives ${ws[k].precision} decimal${ws[k].precision === '1' ? '' : 's'}?`);
        }
        if (ws[k].precision !== null && gs[k].precision === null) {
          return wrong(`Your step shows ${name} with ${ws[k].precision} decimals — which % code gives ${ws[k].precision} decimals?`);
        }
        return wrong(`${name} is ${kindOfSpec(ws[k])} — which % code shows ${kindOfSpec(ws[k])}?`);
      }
      if (ws[k].raw !== gs[k].raw) works = works || ws[k].raw;
      if (canon(g.args[k], false) !== canon(w.args[k], false)) works = works || show(w.args[k]);
    }
  }
  return works ? { verdict: 'works', sameAs: works } : { verdict: 'match' };
}

// ── scanf ─────────────────────────────────────────────────────────────────

/** scanf statements → [{ spec, arg }] pairs, plus how many calls. */
export function scanfPairs(stmts) {
  const pairs = [];
  let calls = 0;
  for (const s of stmts) {
    const c = callOf(s, 'scanf');
    if (!c || !c.args.length || c.args[0].t !== 'str') return null;
    calls += 1;
    const split = splitFormat(unescapeC(c.args[0].v));
    const args = c.args.slice(1);
    if (split.specs.length !== args.length) return { mismatch: { specs: split.specs.length, args: args.length } };
    split.specs.forEach((spec, k) => pairs.push({ spec, arg: args[k], first: k === 0 }));
  }
  return { pairs, calls };
}

const isAddr = (e) => e && e.t === 'addr';
const bare = (e) => (isAddr(e) ? e.x : e);

function checkRead(want, got) {
  const w = scanfPairs(want);
  if (!w || w.mismatch) return null;
  const g = scanfPairs(got);
  if (!g) return wrong('Read it with scanf: scanf("%d", &name);');
  if (g.mismatch) {
    return wrong(`Your scanf has ${g.mismatch.specs} % code${g.mismatch.specs === 1 ? '' : 's'} but ${g.mismatch.args} variable${g.mismatch.args === 1 ? '' : 's'} — one for each % code.`);
  }
  if (g.pairs.length !== w.pairs.length) {
    return wrong(`This step reads ${w.pairs.length} value${w.pairs.length === 1 ? '' : 's'} — yours reads ${g.pairs.length}.`);
  }
  for (let k = 0; k < w.pairs.length; k += 1) {
    const wp = w.pairs[k];
    const gp = g.pairs[k];
    const name = show(bare(wp.arg));
    if (canon(bare(gp.arg)) !== canon(bare(wp.arg))) {
      return wrong(`The ${nth(k)} value read should go into ${name}.`);
    }
    if (isAddr(wp.arg) && !isAddr(gp.arg)) return wrong(`Missing & before ${name} — scanf needs where ${name} lives.`);
    if (!isAddr(wp.arg) && isAddr(gp.arg)) return wrong(`${name} is text (a char array): scanf takes it without &.`);
    const ws = wp.spec;
    const gs = gp.spec;
    const sameConv = ws.conv === gs.conv && ws.len === gs.len;
    if (!sameConv) {
      if (ws.conv === 'f' && ws.len === 'l' && gs.conv === 'f') return wrong(`${name} is a double — scanf needs %lf to read a double.`);
      if (ws.conv.startsWith('[') && gs.conv === 's') {
        // %s stops at a space; the key reads a whole line. Accept with a note.
      } else if (!(ws.conv === 'd' && gs.conv === 'i')) {
        return wrong(`${name} is ${kindOfSpec(ws)} — which % code reads ${kindOfSpec(ws)}?`);
      }
    }
    if (ws.conv === 'c' && ws.skipWs && !gs.skipWs) {
      return wrong(`Put a space before %c (" %c") so it skips the Enter left by the last read.`);
    }
  }
  if (g.calls !== w.calls) {
    return { verdict: 'works', sameAs: null, note: 'one scanf for several values works; the step reads them one scanf at a time' };
  }
  const exact = w.pairs.every((wp, k) => wp.spec.raw === g.pairs[k].spec.raw);
  if (!exact) return { verdict: 'works', sameAs: w.pairs.map((p) => p.spec.raw).join(' ') };
  return { verdict: 'match' };
}

// ── Give back ─────────────────────────────────────────────────────────────

function checkReturn(want, got) {
  if (want.length !== 1 || want[0].s !== 'return') return null;
  const g = got.length === 1 ? got[0] : null;
  if (!g || g.s !== 'return') return wrong('Give it back with return: return value;');
  const we = want[0].e;
  const ge = g.e;
  if (!we && !ge) return { verdict: 'match' };
  if (!we) return wrong('This step only stops the function: return; with no value.');
  if (!ge) return wrong(`This step gives back ${show(we)}: put it after return.`);
  if (canon(ge) !== canon(we)) return wrong('That gives back a different value. Check it against the step.');
  if (canon(ge, false) !== canon(we, false)) return { verdict: 'works', sameAs: `return ${show(we)};` };
  return { verdict: 'match' };
}

// ── Decision ──────────────────────────────────────────────────────────────

/** a < b reads as b > a (and <= as >=), all the way down, so either way round matches. */
function sameWayRound(e) {
  if (!e || typeof e !== 'object') return e;
  if (e.t === 'group') return sameWayRound(e.x);
  if (e.t === 'bin') {
    const l = sameWayRound(e.l);
    const r = sameWayRound(e.r);
    if (e.op === '<') return { ...e, op: '>', l: r, r: l };
    if (e.op === '<=') return { ...e, op: '>=', l: r, r: l };
    return { ...e, l, r };
  }
  if (e.t === 'not') return { ...e, x: sameWayRound(e.x) };
  return e;
}

/** An if / else if / else chain → { conds, bodies, hasElse }, or null when it is not one. */
export function ifChain(stmt) {
  if (!stmt || stmt.s !== 'if') return null;
  const conds = [];
  const bodies = [];
  let hasElse = false;
  let s = stmt;
  while (s && s.s === 'if') {
    conds.push(s.c);
    bodies.push(s.then);
    if (s.else && s.else.s === 'if') { s = s.else; continue; }
    if (s.else) { hasElse = true; bodies.push(s.else); }
    break;
  }
  return { conds, bodies, hasElse };
}

const bodyItems = (b) => (b && b.s === 'block' ? b.items.filter((x) => x.s !== 'empty') : (b && b.s !== 'empty' ? [b] : []));

function checkDecision(want, got) {
  const w = want.length === 1 ? ifChain(want[0]) : null;
  if (!w) return null;
  const g = got.length === 1 ? ifChain(got[0]) : null;
  if (!g) return wrong('Start with if, the check in brackets, then { — if (check) {');
  if (g.bodies.some((b) => bodyItems(b).length)) {
    return wrong('Leave the inside of each { } empty here — the steps inside are written on their own.');
  }
  if (g.conds.length !== w.conds.length) {
    return wrong(w.conds.length === 1
      ? 'This step has one check: if (…) — no else if.'
      : `This step has ${w.conds.length} checks: if (…) then ${w.conds.length - 1} else if (…) — yours has ${g.conds.length}.`);
  }
  for (let k = 0; k < w.conds.length; k += 1) {
    if (canon(sameWayRound(g.conds[k])) !== canon(sameWayRound(w.conds[k]))) {
      return wrong(`The ${k === 0 ? 'if' : `${nth(k - 1)} else if`} check is not the step's${w.conds.length > 1 ? ` ${nth(k)}` : ''} check — compare it with the step.`);
    }
  }
  if (w.hasElse && !g.hasElse) return wrong('The step has an Else: end with } else { }');
  if (!w.hasElse && g.hasElse) return wrong('The step has no Else: take the else off.');
  const exact = w.conds.every((c, k) => canon(c, false) === canon(g.conds[k], false));
  return exact ? { verdict: 'match' } : { verdict: 'works', sameAs: w.conds.map((c) => show(c)).join(', ') };
}

// ── Use your flow ────────────────────────────────────────────────────────

/** `name(args);` or `keep = name(args);` → { fn, args, keep, keepText }, or null. */
export function callParts(stmt) {
  if (!stmt || stmt.s !== 'expr') return null;
  let e = stmt.e;
  while (e && e.t === 'group') e = e.x;
  let keep = null;
  let keepText = '';
  if (e && e.t === 'assign' && e.op === '=') {
    keep = canon(e.l);
    keepText = show(e.l);
    e = e.r;
    while (e && e.t === 'group') e = e.x;
  }
  // `p = (int *) calloc(…)`: the cast is optional in C — the call is what is checked.
  while (e && (e.t === 'cast' || e.t === 'group')) e = e.x;
  if (!e || e.t !== 'call') return null;
  return { fn: e.fn, args: e.args || [], keep, keepText };
}

function checkUse(want, got, { what = 'flow' } = {}) {
  const w = want.length === 1 ? callParts(want[0]) : null;
  if (!w) return null;
  const g = got.length === 1 ? callParts(got[0]) : null;
  const call = `${w.fn}(${w.args.map(() => '…').join(', ')})`;
  if (!g) return wrong(`Use the ${what} by its name, the values in brackets, then ; — ${w.keep ? `${w.keepText} = ` : ''}${call};`);
  if (g.fn !== w.fn) return wrong(`This step uses ${w.fn} — yours uses ${g.fn}.`);
  if (w.keep && !g.keep) return wrong(`What ${w.fn} gives back is kept in ${w.keepText}: ${w.keepText} = ${call};`);
  if (!w.keep && g.keep) return wrong(`This step does not keep what ${w.fn} gives back: just ${call};`);
  if (w.keep && g.keep !== w.keep) return wrong(`Keep it in ${w.keepText} — yours keeps it in ${g.keepText}.`);
  if (g.args.length !== w.args.length) {
    return wrong(`${w.fn} takes ${w.args.length} value${w.args.length === 1 ? '' : 's'} — yours gives ${g.args.length}.`);
  }
  for (let k = 0; k < w.args.length; k += 1) {
    const wa = w.args[k];
    const ga = g.args[k];
    if (canon(ga) === canon(wa)) continue;
    if (isAddr(wa) && !isAddr(ga) && canon(ga) === canon(wa.x)) {
      return wrong(`Missing & before ${show(wa.x)} — ${w.fn} changes ${show(wa.x)}, so it needs where ${show(wa.x)} lives.`);
    }
    if (!isAddr(wa) && isAddr(ga) && canon(ga.x) === canon(wa)) {
      return wrong(`${show(wa)} goes in as a value: no & before it.`);
    }
    return wrong(`The ${nth(k)} value that goes in is not the step's — compare it with the step.`);
  }
  const exact = w.args.every((a, k) => canon(a, false) === canon(g.args[k], false));
  return exact ? { verdict: 'match' } : { verdict: 'works', sameAs: `${w.keep ? `${w.keepText} = ` : ''}${w.fn}(${w.args.map(show).join(', ')});` };
}

// ── Switch ───────────────────────────────────────────────────────────────

/** A switch's own items, in order: [{ k: 'case', v } | { k: 'default' } | { k: 'break' } | { k: 'step' }]. */
function switchItems(stmt) {
  const items = stmt && stmt.body && stmt.body.s === 'block' ? stmt.body.items : [];
  return items.filter((x) => x.s !== 'empty').map((x) => {
    if (x.s === 'case') return { k: 'case', v: canon(x.v), text: show(x.v) };
    if (x.s === 'default') return { k: 'default' };
    if (x.s === 'break') return { k: 'break' };
    return { k: 'step' };
  });
}

function checkSwitch(want, got) {
  const w = want.length === 1 && want[0].s === 'switch' ? want[0] : null;
  if (!w) return null;
  const g = got.length === 1 && got[0].s === 'switch' ? got[0] : null;
  if (!g) return wrong('Start with switch, what it checks in brackets, then { — switch (value) {');
  if (canon(g.e) !== canon(w.e)) return wrong(`This Switch checks ${show(w.e)} — yours checks ${show(g.e)}.`);
  const wi = switchItems(w);
  const gi = switchItems(g);
  if (gi.some((x) => x.k === 'step')) return wrong('Only the case, break and default lines here — the steps inside are written on their own.');
  // Paths: each case (or default) with whether a break ends it.
  const paths = (items) => {
    const out = [];
    items.forEach((x) => {
      if (x.k === 'case' || x.k === 'default') out.push({ ...x, stops: false });
      else if (x.k === 'break' && out.length) out[out.length - 1].stops = true;
    });
    return out;
  };
  const wp = paths(wi);
  const gp = paths(gi);
  const label = (p) => (p.k === 'default' ? 'default:' : `case ${p.text}:`);
  for (let i = 0; i < wp.length; i += 1) {
    const a = wp[i];
    const b = gp[i];
    if (!b) return wrong(a.k === 'default' ? 'The step has an Otherwise: add default: at the end.' : `${label(a)} is missing — the ${nth(i)} path is ${label(a)}`);
    if (a.k !== b.k || (a.k === 'case' && a.v !== b.v)) return wrong(`The ${nth(i)} path is ${label(a)} — yours is ${label(b)}`);
    if (a.k === 'case' && a.stops && !b.stops) return wrong(`After ${label(a)} the path stops here: put break; after it.`);
    if (a.k === 'case' && !a.stops && b.stops) return wrong(`After ${label(a)} the path runs on into the next one: no break; there.`);
  }
  if (gp.length > wp.length) {
    const extra = gp[wp.length];
    return wrong(extra.k === 'default' ? 'The step has no Otherwise: take default: out.' : `${label(extra)} is not one of the step's paths.`);
  }
  // A break after default changes nothing: accepted, said gently.
  const lastW = wp[wp.length - 1];
  const lastG = gp[gp.length - 1];
  if (lastW && lastW.k === 'default' && lastG && lastG.stops) return { verdict: 'works', note: 'a break after default: is not needed (it is the last path)' };
  return { verdict: 'match' };
}

/** A switch's text as one line per item: `switch (x) {`, `case 1:`, `break;`, `default:`, `}`. */
export function splitSwitchLines(text) {
  let out = '';
  let q = null;
  const src = String(text || '');
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (q) {
      out += ch;
      if (ch === '\\' && i + 1 < src.length) { out += src[i + 1]; i += 1; } else if (ch === q) q = null;
      continue;
    }
    if (ch === '"' || ch === "'") { q = ch; out += ch; continue; }
    if (ch === '{') { out += '{\n'; continue; }
    if (ch === '}') { out += '\n}\n'; continue; }
    if (ch === ':' || ch === ';') { out += `${ch}\n`; continue; }
    out += ch;
  }
  return out.split('\n').map((l) => l.trim()).filter(Boolean);
}

// ── Repeat ────────────────────────────────────────────────────────────────

const sameCheck = (a, b) => canon(sameWayRound(a)) === canon(sameWayRound(b));

/** for's first part → { name, start, declared } (int i = 1, or i = 1). */
function forStart(init) {
  if (!init) return null;
  if (init.s === 'decl' && init.decls && init.decls.length === 1) {
    const d = init.decls[0];
    return { name: d.name, start: d.init ? canon(d.init) : null, startText: d.init ? show(d.init) : '', declared: true };
  }
  if (init.s === 'expr' && init.e && init.e.t === 'assign' && init.e.op === '=') {
    return { name: canon(init.e.l), start: canon(init.e.r), startText: show(init.e.r), declared: false };
  }
  return null;
}

function checkLoop(want, got) {
  const w = want.length === 1 ? want[0] : null;
  if (!w || (w.s !== 'while' && w.s !== 'for')) return null;
  const g = got.length === 1 ? got[0] : null;
  const loopWord = w.s === 'while' ? 'while (check) {' : 'for (start; check; step) {';
  if (!g || (g.s !== 'while' && g.s !== 'for' && g.s !== 'do')) return wrong(`Start the loop line with ${loopWord}`);
  if (bodyItems(g.body).length) return wrong('Leave the inside of { } empty here — the steps inside are written on their own.');
  if (w.s === 'while') {
    if (g.s !== 'while') return wrong('This step repeats while a check is true: while (check) { }');
    if (!sameCheck(g.c, w.c)) return wrong('The check that keeps it going is not the step\'s — compare it with the step.');
    return canon(g.c, false) === canon(w.c, false) ? { verdict: 'match' } : { verdict: 'works', sameAs: `while (${show(w.c)})` };
  }
  // A count: for (int i = 1; i <= 10; i++)
  if (g.s !== 'for') return wrong('This step counts: write it as for (start; check; step) { }');
  const ws = forStart(w.init);
  const gs = forStart(g.init);
  if (ws) {
    if (!gs) return wrong(`Start the counter in the first part: for (int ${ws.name} = ${ws.startText}; …)`);
    if (gs.name !== ws.name) return wrong(`The counter is ${ws.name} — yours is ${gs.name}.`);
    if (ws.declared && !gs.declared) return wrong(`Declare the counter where it starts: for (int ${ws.name} = …; …)`);
    if (gs.start !== ws.start) return wrong(`${ws.name} starts at a different value — check where the step starts it.`);
  }
  if (!g.c) return wrong('The middle part is the check that keeps it going — it is missing.');
  if (!sameCheck(g.c, w.c)) return wrong('The check that keeps it going (the middle part) is not the step\'s — compare it with where the step stops.');
  const wStep = w.step ? setParts({ s: 'expr', e: w.step }) : null;
  const gStep = g.step ? setParts({ s: 'expr', e: g.step }) : null;
  if (wStep) {
    if (!gStep) return wrong(`The last part changes the counter each time round: ${wStep.written}`);
    if (gStep.target !== wStep.target || gStep.op !== wStep.op || gStep.value !== wStep.value) {
      return wrong(`The counter should ${wStep.op === '-=' ? 'go down' : 'go up'} by ${wStep.value} each time — check the last part.`);
    }
  }
  const exact = canon(g.c, false) === canon(w.c, false) && (!wStep || gStep.literal === wStep.literal)
    && (!ws || (gs && gs.declared === ws.declared));
  return exact ? { verdict: 'match' } : { verdict: 'works', sameAs: null };
}

/**
 * A block step's text as one line per header (`if (…) {`, `} else if (…) {`,
 * `} else {`, `}`), however the learner spaced it: a break after each { and
 * before each } (outside quotes), an `else` joined to the } before it.
 */
export function splitBlockLines(text) {
  let out = '';
  let q = null;
  const src = String(text || '');
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (q) {
      out += ch;
      if (ch === '\\' && i + 1 < src.length) { out += src[i + 1]; i += 1; } else if (ch === q) q = null;
      continue;
    }
    if (ch === '"' || ch === "'") { q = ch; out += ch; continue; }
    if (ch === '{') { out += '{\n'; continue; }
    if (ch === '}') { out += '\n}'; continue; }
    out += ch;
  }
  const lines = out.split('\n').map((l) => l.trim()).filter(Boolean);
  const joined = [];
  lines.forEach((l) => {
    if (/^else\b/.test(l) && joined.length && joined[joined.length - 1] === '}') joined[joined.length - 1] = `} ${l}`;
    else joined.push(l);
  });
  return joined;
}

// ── generic ───────────────────────────────────────────────────────────────

function stmtCanon(s, loose) {
  if (!s) return '';
  switch (s.s) {
    case 'expr': return `${canon(s.e, loose)};`;
    case 'return': return `return ${s.e ? canon(s.e, loose) : ''};`;
    case 'break': case 'continue': return `${s.s};`;
    default: return JSON.stringify(s, (k, v) => (k === 'line' || k === 'endLine' ? undefined : v));
  }
}

function checkGeneric(want, got) {
  if (want.length !== got.length) return wrong(`This step is ${want.length} line${want.length === 1 ? '' : 's'} of C.`);
  for (let i = 0; i < want.length; i += 1) {
    if (stmtCanon(want[i], true) !== stmtCanon(got[i], true)) return wrong('Not the same as the step yet — check it against the step.');
  }
  const exact = want.every((w, i) => stmtCanon(w, false) === stmtCanon(got[i], false));
  return exact ? { verdict: 'match' } : { verdict: 'works', sameAs: null };
}

// ── the check ─────────────────────────────────────────────────────────────

const wrong = (message) => ({ verdict: 'wrong', message });
// Spacing outside quotes does not count; inside them (" %c") it does.
const squash = (s) => String(s || '').replace(/("(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*')|\s+/g, (m, q) => q || '');

// ── a flow's first line ──────────────────────────────────────────────────

/** A C type as written: float, int *, struct Point. */
export function typeC(t) {
  if (!t) return '?';
  if (t.k === 'ptr') return `${typeC(t.to)} *`;
  if (t.k === 'array') return `${typeC(t.of)} *`;
  return t.name || t.k;
}

/** A C type in words: a decimal, text, where a whole number lives. */
export function typeWords(t) {
  if (!t) return 'a value';
  if (t.k === 'void') return 'nothing';
  if (t.k === 'ptr' || t.k === 'array') {
    const to = t.k === 'ptr' ? t.to : t.of;
    if (to && to.k === 'int' && to.name === 'char') return 'text';
    return `where ${typeWords(to)} lives`;
  }
  if (t.k === 'int') return t.bool ? 'yes or no' : (t.name === 'char' ? 'a character' : 'a whole number');
  if (t.k === 'float') return 'a decimal';
  if (t.k === 'struct') return `a ${String(t.name || '').replace(/^(struct|union)\s+/, '')} record`;
  return 'a value';
}

/** `float hra(float basic) {` → { name, ret, params: [{ name, type }], body } — or { error }. */
export function headerParts(text) {
  const src = String(text || '').trim();
  const tries = [`${src}\n}\n`, `${src}\n`];
  for (const t of tries) {
    try {
      const unit = parseProgram(t);
      const fns = [...unit.functions.values()];
      if (fns.length === 1) {
        const f = fns[0];
        return { name: f.name, ret: f.type.ret, params: (f.params || []).map((p) => ({ name: p.name, type: p.type })), body: f.body };
      }
    } catch (e) { /* try the next */ }
  }
  return { error: true, endsInSemicolon: /;\s*$/.test(src) };
}

/** One parameter as declared: float basic, int *p. */
export const declC = (p) => (typeC(p.type).endsWith(' *') ? `${typeC(p.type)}${p.name}` : `${typeC(p.type)} ${p.name}`);

const sameType = (a, b) => typeC(a).replace(/\s+/g, '') === typeC(b).replace(/\s+/g, '');

function checkFunction(keyText, src) {
  const w = headerParts(keyText);
  if (w.error) return { verdict: 'unreadable', message: "Can't check this step yet." };
  const g = headerParts(src);
  if (g.error) {
    if (g.endsInSemicolon) return wrong(`This is where ${w.name} itself starts: end the line with { — a ; ends the prototype instead.`);
    return { verdict: 'unreadable', message: "Can't read this line — check the brackets and the {" };
  }
  if (g.body && g.body.items && g.body.items.filter((x) => x.s !== 'empty').length) {
    return wrong('Only the first line here — the steps inside are written on their own.');
  }
  return compareSignature(w, g) || { verdict: 'match' };
}

/** What a first line / prototype says, part by part: a wrong(...) or null when it is the same. */
function compareSignature(w, g, { namesOptional = false } = {}) {
  if (g.name !== w.name) return wrong(`This flow is called ${w.name} — yours is called ${g.name}.`);
  if (!sameType(g.ret, w.ret)) {
    return wrong(`${w.name} gives back ${typeWords(w.ret)}: start the line with ${typeC(w.ret)}.`);
  }
  if (g.params.length !== w.params.length) {
    const names = w.params.map((p) => p.name).join(', ');
    return wrong(w.params.length
      ? `${w.name} takes ${w.params.length} value${w.params.length === 1 ? '' : 's'} (${names}) — yours takes ${g.params.length}.`
      : `${w.name} takes nothing: write (void) or ( ).`);
  }
  for (let k = 0; k < w.params.length; k += 1) {
    const wp = w.params[k];
    const gp = g.params[k];
    if (gp.name !== wp.name && !(namesOptional && !gp.name)) return wrong(`The ${nth(k)} value ${w.name} takes is called ${wp.name}.`);
    if (!sameType(gp.type, wp.type)) {
      return wrong(`${wp.name} is ${typeWords(wp.type)}: ${declC(wp)}`);
    }
  }
  return null;
}

/** `float hra(float basic);` → { name, ret, params } — or { error, brace }. */
export function prototypeParts(text) {
  const src = String(text || '').trim();
  try {
    const unit = parseProgram(`${src}\n`);
    if (unit.protos && unit.protos.size === 1 && !unit.functions.size) {
      const d = [...unit.protos.values()][0];
      return { name: d.name, ret: d.type.ret, params: (d.type.params || []).map((p) => ({ name: p.name, type: p.type })) };
    }
    if (unit.functions.size) return { error: true, brace: true };
  } catch (e) {
    try { parseProgram(`${src}\n}\n`); return { error: true, brace: true }; } catch (e2) { /* not a first line either */ }
  }
  return { error: true };
}

function checkPrototype(keyText, src) {
  const w = prototypeParts(keyText);
  if (w.error) return { verdict: 'unreadable', message: "Can't check this step yet." };
  const g = prototypeParts(src);
  if (g.error) {
    if (g.brace) return wrong(`This is the prototype: end it with ; — the { starts ${w.name} itself, on its Start step.`);
    if (!/;\s*$/.test(src) && !prototypeParts(`${src.trim()};`).error) return wrong('Missing ; at the end — a prototype ends with ;');
    return { verdict: 'unreadable', message: "Can't read this line — check the brackets and the ;" };
  }
  const bad = compareSignature(w, g, { namesOptional: true });
  if (bad) return bad;
  return g.params.some((p) => !p.name) && w.params.length
    ? { verdict: 'works', note: 'the names can be left out of a prototype' }
    : { verdict: 'match' };
}

// ── a Resources card: the declarations ───────────────────────────────────

/** A declared type in full: float, int[5], char[20], int *. */
function typeFull(t) {
  if (!t) return '?';
  if (t.k === 'array') return `${typeFull(t.of)}[${t.n === null || t.n === undefined ? '' : t.n}]`;
  if (t.k === 'ptr') return `${typeFull(t.to)}*`;
  return String(t.name || t.k).replace(/\s+/g, ' ');
}
const baseOf = (t) => (t && t.k === 'array' ? baseOf(t.of) : t);

/** Declaration statements → [{ name, type, init }] in order, or null if any line is not one. */
export function declsOf(stmts) {
  const out = [];
  for (const st of stmts) {
    if (!st || st.s !== 'decl') return null;
    st.decls.forEach((d) => out.push({ name: d.name, type: d.type, init: d.init || null }));
  }
  return out;
}

/** How a declaration is written: float price = 49.5; */
export const declLine = (d) => {
  const t = d.type;
  const dims = [];
  let b = t;
  while (b && b.k === 'array') { dims.push(b.n); b = b.of; }
  let stars = '';
  while (b && b.k === 'ptr') { stars += '*'; b = b.to; }
  const name = `${stars}${d.name}${dims.map((n) => `[${n === null || n === undefined ? '' : n}]`).join('')}`;
  return `${b ? b.name : '?'} ${name}${d.init && d.init.t !== 'list' ? ` = ${show(d.init)}` : ''};`;
};

/** The same value in words, for a message. */
export function declWords(d) {
  const t = d.type;
  if (t && t.k === 'array') {
    const base = baseOf(t);
    if (base && base.k === 'int' && base.name === 'char' && t.of === base) return `text of up to ${(t.n || 1) - 1} letters`;
    return `a list of ${t.n} ${typeWords(base).replace(/^an? /, '')}s`;
  }
  return typeWords(t);
}

function checkDeclare(want, got) {
  const w = declsOf(want);
  if (!w) return null;
  const g = declsOf(got);
  if (!g) return wrong('Only declarations here: a type, a name, then ; — for example int count;');
  const byName = new Map(g.map((d) => [d.name, d]));
  for (const wd of w) {
    const gd = byName.get(wd.name);
    if (!gd) return wrong(`${wd.name} is missing — it is ${declWords(wd)}: ${declLine(wd)}`);
  }
  const extra = g.find((d) => !w.some((x) => x.name === d.name));
  if (extra) return wrong(`${extra.name} is not one of these values — take it out.`);
  let works = null;
  for (const wd of w) {
    const gd = byName.get(wd.name);
    if (typeFull(gd.type) !== typeFull(wd.type)) {
      const wb = baseOf(wd.type);
      const gb = baseOf(gd.type);
      if (wd.type.k === 'array' && gd.type.k === 'array' && typeFull(wb) === typeFull(gb)) {
        return wrong(`${wd.name} holds ${declWords(wd)}: ${declLine(wd)}`);
      }
      return wrong(`${wd.name} is ${declWords(wd)}: ${declLine(wd)}`);
    }
    if (wd.init && !gd.init) return wrong(`${wd.name} starts at ${show(wd.init)}: ${declLine(wd)}`);
    if (wd.init && gd.init && canon(wd.init) !== canon(gd.init)) return wrong(`${wd.name} starts at ${show(wd.init)}, not ${show(gd.init)}.`);
    if (!wd.init && gd.init) works = works || `${wd.name} needs no start value here`;
  }
  const sameOrder = w.every((d, i) => g[i] && g[i].name === d.name);
  if (works) return { verdict: 'works', note: works };
  return sameOrder ? { verdict: 'match' } : { verdict: 'works', note: 'in another order, which is fine' };
}

/**
 * Check a learner's C for one step.
 *
 * @param {{ type: string, expectedLines: string[], text: string, sentence?: string }} args
 * @returns {{ verdict: 'match'|'works'|'wrong'|'unreadable'|'empty', message: string, sameAs?: string, line?: number }}
 */
export function checkStep({ type, expectedLines, text, sentence = '' }) {
  const src = String(text || '');
  if (!src.trim()) return { verdict: 'empty', message: 'Write the C for this step first.' };
  const keyText = (expectedLines || []).join('\n');
  if (type === 'function' || type === 'prototype') {
    const out = type === 'function' ? checkFunction(keyText, src) : checkPrototype(keyText, src);
    return { ...out, message: out.message && out.verdict !== 'wrong' ? out.message : verdictMessage(out) };
  }
  const key = tryRead(keyText);
  if (key.error) return { verdict: 'unreadable', message: "Can't check this step yet." };

  const mine = tryRead(src);
  if (mine.error) {
    // The commonest slip, named: a statement without its ;
    const lines = src.split('\n');
    const at = mine.line && lines[mine.line - 1] !== undefined ? mine.line : lines.length;
    if (!/;\s*$/.test(src) && !tryRead(src.replace(/\s*$/, ';')).error) {
      return { verdict: 'wrong', message: '✗ Missing ; at the end of the line.', line: lines.length };
    }
    return { verdict: 'unreadable', message: "Can't read this line — check the brackets, quotes and ;", line: at };
  }

  let out = null;
  if (squash(src) === squash(keyText)) out = { verdict: 'match' };
  else if (type === 'assignment') out = checkSet(key.stmts, mine.stmts, { sentence });
  else if (type === 'output') out = checkShow(key.stmts, mine.stmts);
  else if (type === 'input') out = checkRead(key.stmts, mine.stmts);
  else if (type === 'return') out = checkReturn(key.stmts, mine.stmts);
  else if (type === 'decision') out = checkDecision(key.stmts, mine.stmts);
  else if (type === 'loop') out = checkLoop(key.stmts, mine.stmts);
  else if (type === 'subflow') out = checkUse(key.stmts, mine.stmts);
  else if (type === 'action') out = checkUse(key.stmts, mine.stmts, { what: 'function' });
  else if (type === 'switch') out = checkSwitch(key.stmts, mine.stmts);
  else if (type === 'declare') out = checkDeclare(key.stmts, mine.stmts);
  if (!out) out = checkGeneric(key.stmts, mine.stmts);
  return { ...out, message: verdictMessage(out) };
}

/** The words a verdict is shown in. */
export function verdictMessage(v) {
  if (!v) return '';
  if (v.verdict === 'match') return '✓ matches';
  if (v.verdict === 'works') {
    if (v.note) return `✓ works — ${v.note}`;
    return v.sameAs ? `✓ works — same as ${v.sameAs}` : '✓ works';
  }
  if (v.verdict === 'wrong') return `✗ ${v.message}`;
  return v.message || '';
}

export const isPassing = (verdict) => verdict === 'match' || verdict === 'works';
